/**
 * Retry utility with exponential backoff for Supabase requests
 * Provides automatic retry logic with configurable parameters and state tracking
 */

const DEFAULT_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  retryableErrors: [
    'PGRST301', // JWT expired
    'PGRST116', // Multiple rows returned
    'ETIMEDOUT',
    'ECONNRESET',
    'ENOTFOUND',
    'Network request failed',
    'Failed to fetch'
  ]
};

/**
 * Check if error is retryable
 */
const isRetryableError = (error, retryableErrors) => {
  if (!error) return false;
  
  const errorMessage = error?.message || error?.toString() || '';
  const errorCode = error?.code || error?.status;
  
  // Check for network errors
  if (errorMessage?.includes('network') || errorMessage?.includes('fetch')) {
    return true;
  }
  
  // Check for timeout errors
  if (errorMessage?.includes('timeout') || errorCode === 'ETIMEDOUT') {
    return true;
  }
  
  // Check against configured retryable errors
  return retryableErrors?.some(retryableError => 
    errorMessage?.includes(retryableError) || errorCode === retryableError
  );
};

/**
 * Calculate delay with exponential backoff and jitter
 */
const calculateDelay = (attempt, initialDelay, maxDelay, backoffMultiplier) => {
  const exponentialDelay = initialDelay * Math.pow(backoffMultiplier, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay; // Add 0-30% jitter
  return Math.min(exponentialDelay + jitter, maxDelay);
};

/**
 * Sleep utility
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retry wrapper with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {Object} config - Retry configuration
 * @param {Function} onRetry - Callback for retry events (attempt, delay, error)
 * @returns {Promise} - Result of the function
 */
export const retryWithBackoff = async (fn, config = {}, onRetry = null) => {
  const {
    maxRetries,
    initialDelay,
    maxDelay,
    backoffMultiplier,
    retryableErrors
  } = { ...DEFAULT_CONFIG, ...config };

  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      return result;
    } catch (error) {
      lastError = error;
      
      // Don't retry if it's the last attempt or error is not retryable
      if (attempt === maxRetries || !isRetryableError(error, retryableErrors)) {
        throw error;
      }
      
      // Calculate delay for next retry
      const delay = calculateDelay(attempt, initialDelay, maxDelay, backoffMultiplier);
      
      // Notify about retry
      if (onRetry) {
        onRetry({
          attempt: attempt + 1,
          maxRetries,
          delay,
          error: error?.message || 'Unknown error'
        });
      }
      
      console.warn(`Retry attempt ${attempt + 1}/${maxRetries} after ${Math.round(delay)}ms:`, error?.message);
      
      // Wait before retrying
      await sleep(delay);
    }
  }
  
  throw lastError;
};

/**
 * Create a retry-enabled version of a function
 */
export const withRetry = (fn, config = {}) => {
  return async (...args) => {
    return retryWithBackoff(() => fn(...args), config);
  };
};

export default retryWithBackoff;